// app/src/js/attendance/attendance.js
import { state } from '../state.js';
import { utils, showConfirm } from '../utils.js';
import { refreshTopicInfo } from './topics.js';

export const renderAttendanceList = (topicDay) => {
    state.currentTopicDay = topicDay.id;
    const isMobile = utils.isMobile();
    const modal = document.getElementById('attendanceModal');

    // Determine container
    const container = isMobile
        ? modal.querySelector('.modal-body')
        : document.getElementById('peopleGrid');

    // Generate cards HTML
    const cardsHTML = topicDay.attendance.map(record => {
        const child = state.data.children.find(c => c.id === record.child_id);
        if (!child) return '';
        const status = record.attended;

        return `
            <div class="attendance-card" id="card-${child.id}">
                <div class="card-content">
                    <div class="participant-avatar">
                        ${child.photo_url ? `<img src="${child.photo_url}">` : '<i class="bi bi-person-fill"></i>'}
                    </div>
                    <div class="participant-info">
                        <h3>${child.first_name} ${child.last_name}</h3>
                        <div class="status-box">
                            <span>Attendance:</span>
                            <div class="status-indicator">
                                ${status === 1 ? '<i class="bi bi-check-lg text-success"></i>' : ''}
                                ${status === 0 ? '<span class="text-danger fw-bold">A</span>' : ''}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <div class="action-links" data-child-id="${child.id}" data-attendance-id="${record.id}">
                        ${status === null ? `
                            <span class="link-btn text-primary btn-present">Present</span>
                            <span class="link-btn text-primary btn-absent">Absent</span>
                        ` : `
                            <span class="link-btn text-muted btn-edit">Edit attendance</span>
                        `}
                    </div>
                    <button class="btn-notes" data-attendance-id="${record.id}">Notes (${record.note_count})</button>
                </div>
                <div class="notes-area d-none">
                    <div class="note-form">
                        <textarea class="note-textarea" placeholder="Add a note..." rows="2"></textarea>
                        <div class="note-form-actions">
                            <button class="btn-save-note">Save</button>
                            <button class="btn-cancel-note">Cancel</button>
                        </div>
                    </div>
                    <div class="notes-list"></div>
                </div>
                <div class="card-loader d-none">
                    <div class="spinner"></div>
                </div>
            </div>
        `;

    }).join('');

    container.innerHTML = cardsHTML;

    const topic = state.data.topics.find(t => t.id === topicDay.topic_id);
    if (isMobile) {
        document.getElementById('modalTopicTitle').innerText = topic.title;
        modal.classList.add('is-visible');

        // 1. Lock body scroll
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        // 2. IMPORTANT: Reset modal scroll to top every time it opens
        modal.querySelector('.modal-body').scrollTop = 0;
        history.pushState({ modal: 'attendanceModal' }, '', location.pathname);
    } else {
        document.getElementById('desktopTitle').innerText = topic.title;
        document.querySelector('.details-column')?.classList.remove('show-report');
    }

    setupAttendanceListeners(container);
};

export const resetAttendanceList = () => {
    document.getElementById('peopleGrid').innerHTML = "";
    document.getElementById('desktopTitle').innerText = "Select a day to add the attendance";
    document.querySelector('.details-column')?.classList.remove('show-report');
};

// Function to CLOSE the modal (assign to the back arrow icon)
const closeModalBtn = document.querySelector('#attendanceModal .bi-chevron-left');
if (closeModalBtn) {
    closeModalBtn.addEventListener('click', () => {
        history.back();
    });
}

// Listen for clicks on the cards container
let attendanceListenerController = null;

const setupAttendanceListeners = (container) => {
    if (attendanceListenerController) {
        attendanceListenerController.abort();
    }
    attendanceListenerController = new AbortController();

    container.addEventListener('click', async (e) => {

        // --- Attendance actions ---
        const btn = e.target.closest('.link-btn');
        if (btn) {
            const card = btn.closest('.attendance-card');
            const attendanceId = btn.parentElement.dataset.attendanceId;
            const loader = card.querySelector('.card-loader');

            if (btn.classList.contains('btn-present')) {
                await handleSaveAttendance(attendanceId, true, card, loader);
            } else if (btn.classList.contains('btn-absent')) {
                await handleSaveAttendance(attendanceId, false, card, loader);
            } else if (btn.classList.contains('btn-edit')) {
                showEditOptions(card);
            }
            return;
        }

        // --- Notes toggle ---
        const notesBtn = e.target.closest('.btn-notes');
        if (notesBtn) {
            const card = notesBtn.closest('.attendance-card');
            const notesArea = card.querySelector('.notes-area');
            if (!notesArea.classList.contains('d-none')) {
                notesArea.classList.add('d-none');
                return;
            }
            await openNotes(card, notesBtn.dataset.attendanceId);
            return;
        }

        // --- Save note ---
        const saveNoteBtn = e.target.closest('.btn-save-note');
        if (saveNoteBtn) {
            const card = saveNoteBtn.closest('.attendance-card');
            const attendanceId = card.querySelector('.btn-notes').dataset.attendanceId;
            const textarea = card.querySelector('.note-textarea');
            await saveNote(card, attendanceId, textarea.value.trim());
            return;
        }

        // --- Cancel note form ---
        const cancelNoteBtn = e.target.closest('.btn-cancel-note');
        if (cancelNoteBtn) {
            cancelNoteBtn.closest('.attendance-card').querySelector('.note-textarea').value = '';
            return;
        }

        // --- Delete note ---
        const deleteNoteBtn = e.target.closest('.btn-delete-note');
        if (deleteNoteBtn) {
            const noteItem = deleteNoteBtn.closest('.note-item');
            const rawText = noteItem.querySelector('.note-text').textContent.trim();
            const preview = rawText.length > 150 ? rawText.slice(0, 150) + '…' : rawText;
            const confirmed = await showConfirm({
                title: 'Delete note',
                message: `This note will be permanently removed.<span class="confirm-note-preview">"${escapeHtml(preview)}"</span>`,
                confirmText: 'Delete',
                iconClass: 'bi-trash',
            });
            if (!confirmed) return;
            const card = deleteNoteBtn.closest('.attendance-card');
            const attendanceId = card.querySelector('.btn-notes').dataset.attendanceId;
            await handleDeleteNote(attendanceId, noteItem.dataset.noteId, card);
            return;
        }
    }, { signal: attendanceListenerController.signal });
};

const handleSaveAttendance = async (attendanceId, status, card, loader) => {
    loader.classList.remove('d-none');

    const record = state.getCurrentTopicDay().attendance.find(a => a.id == attendanceId);

    const payload = {
        attendance_id: record.id,
        attended: status
    };

    try {
        const response = await fetch(`${window.AppSettings.uriRoot}ajax/update_attendance`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error('Network error');

        const result = await response.json();

        if (result.success) {
            updateLocalState(record, result.attended);
            updateCardUI(card, result.attended);
        } else {
            throw new Error(result.message || 'Error saving');
        }

    } catch (error) {
        console.error("Sync error:", error);
        alert("Could not save attendance. Check your connection.");
    } finally {
        loader.classList.add('d-none');
    }
};

const updateCardUI = (card, status) => {
    const indicator = card.querySelector('.status-indicator');
    indicator.innerHTML = status === 1
        ? '<i class="bi bi-check-lg text-success fs-5"></i>'
        : '<span class="text-danger fw-bold fs-5">A</span>';

    const actionArea = card.querySelector('.action-links');
    actionArea.innerHTML = `<span class="link-btn text-muted btn-edit">Edit attendance</span>`;

    refreshTopicInfo();
};

const showEditOptions = (card) => {
    const actionArea = card.querySelector('.action-links');
    actionArea.innerHTML = `
        <span class="link-btn text-primary btn-present">Present</span>
        <span class="link-btn text-primary btn-absent">Absent</span>
    `;
};

const openNotes = async (card, attendanceId) => {
    const notesArea = card.querySelector('.notes-area');
    const notesList = notesArea.querySelector('.notes-list');
    notesArea.querySelector('.note-textarea').value = '';
    notesList.innerHTML = '<p class="notes-loading">Loading...</p>';
    notesArea.classList.remove('d-none');

    try {
        const response = await fetch(`${window.AppSettings.uriRoot}ajax/get_notes/${attendanceId}`);
        const result = await response.json();
        renderNotesList(notesList, result.notes);
        updateNotesBtn(card, result.notes.length);
    } catch {
        notesList.innerHTML = '<p class="notes-error">Could not load notes.</p>';
    }
};

const saveNote = async (card, attendanceId, note) => {
    if (!note) return;

    try {
        const response = await fetch(`${window.AppSettings.uriRoot}ajax/add_note`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attendance_id: Number(attendanceId), note }),
        });
        const result = await response.json();
        if (result.success) {
            renderNotesList(card.querySelector('.notes-list'), result.notes);
            updateNotesBtn(card, result.notes.length);
            card.querySelector('.note-textarea').value = '';
        }
    } catch {
        console.error('Could not save note');
    }
};

const renderNotesList = (notesList, notes) => {
    if (!notes.length) {
        notesList.innerHTML = '<p class="notes-empty">No notes yet.</p>';
        return;
    }
    notesList.innerHTML = [...notes].reverse().map(n => `
        <div class="note-item" data-note-id="${n.id}">
            <div class="note-meta">
                <span class="note-meta-text">${escapeHtml(n.first_name)} ${escapeHtml(n.last_name)} &middot; ${formatNoteDate(n.created_at)}</span>
                <button class="btn-delete-note"><i class="bi bi-trash"></i></button>
            </div>
            <div class="note-text">${escapeHtml(n.note)}</div>
        </div>
    `).join('');
};

const handleDeleteNote = async (attendanceId, noteId, card) => {
    try {
        const response = await fetch(`${window.AppSettings.uriRoot}ajax/delete_note`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note_id: Number(noteId), attendance_id: Number(attendanceId) }),
        });
        const result = await response.json();
        if (result.success) {
            renderNotesList(card.querySelector('.notes-list'), result.notes);
            updateNotesBtn(card, result.notes.length);
        }
    } catch {
        console.error('Could not delete note');
    }
};

const updateNotesBtn = (card, count) => {
    const btn = card.querySelector('.btn-notes');
    btn.textContent = `Notes (${count})`;
};

const formatNoteDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const escapeHtml = (str) => {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
};

/**
 * Updates the attendance value in the current topicDay object
 */
const updateLocalState = (record, status) => {
    if (record) {
        record.attended = status;
    } else {
        console.error("Attendance record not found");
    }
};
